import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Container,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  useTheme,
  Grid,
  Fade,
  Card,
  CardContent,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Collapse,
  Zoom,
  Slide,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatIcon from "@mui/icons-material/Chat";
import VideocamIcon from "@mui/icons-material/Videocam";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { baseURL } from "../../../config";
import { useDispatch } from "react-redux";
import { showAppAlert } from "../../../store/reducers/appreducer";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";

const InterviewPage = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const hiddenLinkRef = useRef(null);
  const fileInputRef = useRef(null);

  // State management
  const [chatUrl, setChatUrl] = useState(null);
  const [zoomUrl, setZoomUrl] = useState(null);
  const [showSettings, setShowSettings] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const [interviewMode, setInterviewMode] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  // Enhanced interview config state
  const [interviewConfig, setInterviewConfig] = useState({
    interview_voice_type: "male",
    interview_style: "formal",
    interview_duration: "short",
    interview_interaction_mode: "open-ended",
    interview_language: "english",
    interview_difficulty: "medium",
    voice_id: "1",
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const steps = [
    { label: "Upload Resume", icon: <UploadFileIcon /> },
    { label: "Configure Interview", icon: <SettingsIcon /> },
    { label: "Schedule Interview", icon: <ScheduleIcon /> },
  ];

  const modes = [
    {
      value: "phone",
      label: "Phone Interview",
      icon: <PhoneIcon color="primary" />,
      description: "Voice-based interview with AI assistant",
      color: "#2196f3",
    },
    {
      value: "chat",
      label: "Chat Interview",
      icon: <ChatIcon color="primary" />,
      description: "Text-based interview in real-time",
      color: "#4caf50",
    },
    {
      value: "zoom",
      label: "Video Interview",
      icon: <VideocamIcon color="primary" />,
      description: "Video conference with AI interviewer",
      color: "#ff9800",
    },
  ];

  // Enhanced validation function
  const validateAndFormatPhoneNumber = (phoneNumber) => {
    const digitsOnly = phoneNumber.replace(/\D/g, "");

    const validationResult = {
      isValid: false,
      formattedNumber: "",
      error: "",
    };

    if (!digitsOnly) {
      validationResult.error = "Phone number is required";
      return validationResult;
    }

    if (digitsOnly.length < 10) {
      validationResult.error = "Phone number must be at least 10 digits";
      return validationResult;
    }

    if (digitsOnly.length > 15) {
      validationResult.error = "Phone number cannot exceed 15 digits";
      return validationResult;
    }

    validationResult.isValid = true;
    validationResult.formattedNumber = `+${digitsOnly}`;

    return validationResult;
  };

  // Enhanced file handling with drag and drop
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    handleFile(file);
  };

  const handleFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5MB limit

    if (!file) {
      setError("Please select a file");
      return;
    }

    if (file.type !== "application/pdf") {
      setError("Please upload a PDF file only");
      setSelectedFile(null);
      return;
    }

    if (file.size > maxSize) {
      setError("File size should be less than 5MB");
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setError("");
    setActiveStep(1);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setActiveStep(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openInNewTab = (url) => {
    const newWindow = window.open(url, "_blank");

    if (
      !newWindow ||
      newWindow.closed ||
      typeof newWindow.closed === "undefined"
    ) {
      const form = document.createElement("form");
      form.method = "POST";
      form.action = url;
      form.target = "_blank";
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError("Please upload a resume first");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setActiveStep(2);

    const formData = new FormData();
    formData.append("files", selectedFile);

    // Add interview config to formData only if not in chat mode
    if (interviewMode !== "chat") {
      Object.keys(interviewConfig).forEach((key) => {
        formData.append(key, interviewConfig[key]);
      });
    }

    const modeMap = {
      phone: "P",
      zoom: "Z",
      chat: "C",
    };

    try {
      // Build query parameters based on mode
      const queryParams = new URLSearchParams({
        interviewMode: modeMap[interviewMode],
      });

      // Add phone number for both phone and zoom modes
      if (interviewMode === "phone" || interviewMode === "zoom") {
        if (!phoneNumber) {
          setError("Please enter a phone number");
          dispatch(
            showAppAlert({
              show: true,
              alertType: "warning",
              alertMessage: "Please enter a phone number",
            })
          );
          setLoading(false);
          return;
        }

        const phoneValidation = validateAndFormatPhoneNumber(phoneNumber);
        if (!phoneValidation.isValid) {
          setError(phoneValidation.error);
          setLoading(false);
          return;
        }

        queryParams.append("Phone", phoneValidation.formattedNumber);
      }

      const response = await fetch(
        `${baseURL}instant-interview?${queryParams}`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Something went wrong");
      }

      const data = await response.json();

      // Set appropriate success messages based on mode
      switch (interviewMode) {
        case "phone":
          setSuccess(
            "Phone interview has been initiated. You will receive a call shortly."
          );
          dispatch(
            showAppAlert({
              show: true,
              alertType: "success",
              alertMessage:
                "Phone interview has been initiated. You will receive a call shortly.",
            })
          );
          break;
        case "zoom":
          if (data.error === false && data["Join URL"]) {
            setSuccess("Zoom interview has been initiated.");
            setZoomUrl(data["Join URL"]);
            setShowSettings(false);
            dispatch(
              showAppAlert({
                show: true,
                alertType: "success",
                alertMessage:
                  "Zoom interview has been initiated. Click the button below to join.",
              })
            );
          } else {
            dispatch(
              showAppAlert({
                show: true,
                alertType: "error",
                alertMessage: "Zoom URL not received",
              })
            );
            throw new Error("Zoom URL not received");
          }
          break;
        case "chat":
          if (data.error === false && data["Chat URL"]) {
            setSuccess("Chat interview has been initiated");
            setChatUrl(data["Chat URL"]);
            setShowSettings(false);
            dispatch(
              showAppAlert({
                show: true,
                alertType: "success",
                alertMessage:
                  "Chat interview has been initiated. Click the button below to start chatting.",
              })
            );
          } else {
            dispatch(
              showAppAlert({
                show: true,
                alertType: "error",
                alertMessage: "Chat URL not received",
              })
            );
            throw new Error("Chat URL not received");
          }
          break;
        default:
          setSuccess(data.message);
          dispatch(
            showAppAlert({
              show: true,
              alertType: "success",
              alertMessage: data.message,
            })
          );
          break;
      }
    } catch (err) {
      setError(err.message || "An error occurred");
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: err.message || "An error occurred",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case "phone":
        return <PhoneIcon />;
      case "chat":
        return <ChatIcon />;
      case "zoom":
        return <VideocamIcon />;
      default:
        return <PhoneIcon />;
    }
  };

  const handleUrlClick = (url) => {
    window.open(url, "_blank");
    setShowSettings(true);
    setChatUrl(null);
    setZoomUrl(null);
    setActiveStep(0);
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPhoneNumber("");
    setInterviewConfig({
      interview_voice_type: "male",
      interview_style: "formal",
      interview_duration: "short",
      interview_interaction_mode: "open-ended",
      interview_language: "english",
      interview_difficulty: "medium",
      voice_id: "1",
    });
    setActiveStep(0);
    setShowSettings(true);
    setChatUrl(null);
    setZoomUrl(null);
    setError("");
    setSuccess("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Auto-advance step when file is uploaded
  useEffect(() => {
    if (selectedFile && activeStep === 0) {
      setActiveStep(1);
    }
  }, [selectedFile, activeStep]);

  return (
    <Container maxWidth="md" sx={{ py: 1 }}>
      {/* Hidden link for chat URL */}
      <a
        ref={hiddenLinkRef}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "none" }}
      />

      <Fade in={true} timeout={800}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "background.paper",
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          }}
        >
          {/* Enhanced Header Section */}
          <Box
            sx={{
              p: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 100,
                height: 100,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": { transform: "scale(1)", opacity: 0.5 },
                  "50%": { transform: "scale(1.1)", opacity: 0.3 },
                  "100%": { transform: "scale(1)", opacity: 0.5 },
                },
              }}
            />
            <Typography
              variant="h6"
              fontWeight="600"
              sx={{
                mb: 0.5,
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                letterSpacing: "0.3px",
              }}
            >
              Instant Interview
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 300 }}>
              AI-Powered Candidate Assessment
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.8 }}>
              Upload a resume and start interviewing candidates in minutes
            </Typography>
          </Box>

          {/* Main Content */}
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {/* File Upload Section */}
              <Grid item xs={12}>
                <Slide direction="up" in={true} timeout={600}>
                  <Card
                    elevation={isDragging ? 8 : 2}
                    sx={{
                      border: "2px dashed",
                      borderColor: isDragging
                        ? theme.palette.primary.main
                        : selectedFile
                        ? theme.palette.success.main
                        : theme.palette.primary.light,
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      transform: isDragging ? "scale(1.02)" : "scale(1)",
                      bgcolor: isDragging
                        ? "rgba(25, 118, 210, 0.05)"
                        : "background.paper",
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <CardContent sx={{ p: 2, textAlign: "center" }}>
                      {selectedFile ? (
                        <Zoom in={true} timeout={500}>
                          <Box sx={{ textAlign: "center" }}>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                              }}
                            >
                              <CheckCircleIcon
                                color="success"
                                sx={{ fontSize: 36 }}
                              />
                            </Box>
                            <Typography
                              variant="h6"
                              gutterBottom
                              fontWeight="600"
                            >
                              Resume Uploaded Successfully
                            </Typography>
                            <Chip
                              label={selectedFile.name}
                              color="primary"
                              variant="outlined"
                              sx={{ mb: 2 }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              File size:{" "}
                              {(selectedFile.size / (1024 * 1024)).toFixed(2)}{" "}
                              MB
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                justifyContent: "center",
                              }}
                            >
                              <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                startIcon={<UploadFileIcon />}
                              >
                                Choose Different File
                                <input
                                  ref={fileInputRef}
                                  type="file"
                                  hidden
                                  accept=".pdf"
                                  onChange={handleFileChange}
                                />
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={removeFile}
                              >
                                Remove File
                              </Button>
                            </Box>
                          </Box>
                        </Zoom>
                      ) : (
                        <Box>
                          <Box
                            sx={{
                              width: 70,
                              height: 70,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              opacity: 0.8,
                            }}
                          >
                            <CloudUploadIcon
                              color="primary"
                              sx={{ fontSize: 36 }}
                            />
                          </Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            fontWeight="600"
                          >
                            Upload Candidate's Resume
                          </Typography>
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                          >
                            Drag and drop a PDF file here or click to browse
                          </Typography>
                          <Button
                            variant="contained"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            size="medium"
                            sx={{
                              borderRadius: 2,
                              px: 3,
                              py: 1,
                              fontSize: "1rem",
                              fontWeight: "500",
                            }}
                          >
                            Choose PDF File
                            <input
                              ref={fileInputRef}
                              type="file"
                              hidden
                              accept=".pdf"
                              onChange={handleFileChange}
                            />
                          </Button>
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 2, color: "text.secondary" }}
                          >
                            Maximum file size: 5MB • Supported format: PDF only
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>

              {/* Error/Success Alerts */}
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Grid>
              )}

              {success && (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ borderRadius: 2 }}>
                    {success}
                  </Alert>
                </Grid>
              )}

              {showSettings ? (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }}>
                      <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        sx={{ px: 2, fontWeight: "500" }}
                      >
                        Interview Settings
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="600"
                      gutterBottom
                      sx={{ mb: 2 }}
                    >
                      Select Interview Mode
                    </Typography>
                    <Grid container spacing={2}>
                      {modes.map((mode) => (
                        <Grid item xs={12} md={4} key={mode.value}>
                          <Card
                            elevation={interviewMode === mode.value ? 4 : 1}
                            sx={{
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              border:
                                interviewMode === mode.value
                                  ? `2px solid ${mode.color}`
                                  : "2px solid transparent",
                              transform:
                                interviewMode === mode.value
                                  ? "scale(1.02)"
                                  : "scale(1)",
                              minHeight: 210,
                              height: 210,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "stretch",
                              "&:hover": {
                                transform: "scale(1.02)",
                                elevation: 4,
                              },
                            }}
                            onClick={() => setInterviewMode(mode.value)}
                          >
                            <CardContent
                              sx={{
                                p: 2,
                                textAlign: "center",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 45,
                                  height: 45,
                                  borderRadius: "50%",
                                  bgcolor: `${mode.color}20`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mx: "auto",
                                  mb: 1.5,
                                }}
                              >
                                {mode.icon}
                              </Box>
                              <Typography
                                variant="subtitle1"
                                fontWeight="600"
                                gutterBottom
                              >
                                {mode.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ flexGrow: 1 }}
                              >
                                {mode.description}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Grid container spacing={3}>
                      {/* Voice Type */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Voice Type</InputLabel>
                          <Select
                            value={interviewConfig.interview_voice_type}
                            label="Voice Type"
                            onChange={(e) =>
                              setInterviewConfig((prev) => ({
                                ...prev,
                                interview_voice_type: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="male">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <MaleIcon color="primary" /> Male
                              </Box>
                            </MenuItem>
                            <MenuItem value="female">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <FemaleIcon color="secondary" /> Female
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Voice Id */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Voice Id</InputLabel>
                          <Select
                            value={interviewConfig.voice_id}
                            label="Voice Id"
                            onChange={(e) =>
                              setInterviewConfig((prev) => ({
                                ...prev,
                                voice_id: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="1">Voice 1</MenuItem>
                            <MenuItem value="2">Voice 2</MenuItem>
                            <MenuItem value="3">Voice 3</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Interview Style */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Interview Style</InputLabel>
                          <Select
                            value={interviewConfig.interview_style}
                            label="Interview Style"
                            onChange={(e) =>
                              setInterviewConfig((prev) => ({
                                ...prev,
                                interview_style: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="formal">Formal</MenuItem>
                            <MenuItem value="friendly">Friendly</MenuItem>
                            <MenuItem value="casual">Casual</MenuItem>
                            <MenuItem value="empathetic">Empathetic</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Duration */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Duration</InputLabel>
                          <Select
                            value={interviewConfig.interview_duration}
                            label="Duration"
                            onChange={(e) =>
                              setInterviewConfig((prev) => ({
                                ...prev,
                                interview_duration: e.target.value,
                              }))
                            }
                            startAdornment={
                              <ScheduleIcon color="primary" sx={{ mr: 1 }} />
                            }
                          >
                            <MenuItem value="short">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                Short
                              </Box>
                            </MenuItem>
                            <MenuItem value="medium">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                Medium
                              </Box>
                            </MenuItem>
                            <MenuItem value="long">
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                Long
                              </Box>
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Interaction Mode */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth variant="outlined">
                          <InputLabel>Interaction Mode</InputLabel>
                          <Select
                            value={interviewConfig.interview_interaction_mode}
                            label="Interaction Mode"
                            onChange={(e) =>
                              setInterviewConfig((prev) => ({
                                ...prev,
                                interview_interaction_mode: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="open-ended">Open-ended</MenuItem>
                            <MenuItem value="follow-up-driven">
                              Follow-up Driven
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      {/* Candidate's Phone Number (if phone/zoom) */}
                      {(interviewMode === "phone" ||
                        interviewMode === "zoom") && (
                        <Grid item xs={12} md={6}>
                          {/* <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            gutterBottom
                            sx={{ mb: 1.5 }}
                          >
                            Candidate's Phone Number
                          </Typography> */}
                          <PhoneInput
                            country={"us"}
                            value={phoneNumber}
                            onChange={(value) => {
                              setPhoneNumber(value);
                              const validation =
                                validateAndFormatPhoneNumber(value);
                              setIsPhoneValid(validation.isValid);
                              if (!validation.isValid) {
                                setError(validation.error);
                              } else {
                                setError("");
                              }
                            }}
                            inputStyle={{
                              width: "100%",
                              height: "56px",
                              fontSize: "16px",
                              borderRadius: "12px",
                              borderColor: isPhoneValid
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                              borderWidth: "2px",
                            }}
                            containerStyle={{
                              width: "100%",
                            }}
                            buttonStyle={{
                              borderRadius: "12px 0 0 12px",
                              borderColor: isPhoneValid
                                ? theme.palette.success.main
                                : theme.palette.error.main,
                              borderWidth: "2px",
                            }}
                          />
                          {!isPhoneValid && phoneNumber && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ mt: 1, display: "block" }}
                            >
                              Please enter a valid phone number
                            </Typography>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={
                          loading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            getModeIcon(interviewMode)
                          )
                        }
                        onClick={handleSubmit}
                        disabled={
                          loading ||
                          !selectedFile ||
                          ((interviewMode === "phone" ||
                            interviewMode === "zoom") &&
                            (!phoneNumber || !isPhoneValid))
                        }
                        sx={{
                          maxWidth: 900,
                          fontWeight: 700,
                          fontSize: "1.05rem",
                          borderRadius: 2.5,
                          py: 1.2,
                          background:
                            "linear-gradient(90deg, #16c08e 0%, #11998e 100%)",
                          boxShadow: "0px 4px 16px 0px #11998e33",
                          textTransform: "none",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover": {
                            background:
                              "linear-gradient(90deg, #11998e 0%, #16c08e 100%)",
                            boxShadow: "0px 6px 20px 0px #11998e44",
                          },
                          "& .MuiButton-startIcon": {
                            marginRight: 2,
                          },
                        }}
                      >
                        {loading
                          ? "Processing..."
                          : `Schedule ${
                              interviewMode.charAt(0).toUpperCase() +
                              interviewMode.slice(1)
                            } Interview`}
                      </Button>
                    </Box>
                  </Grid>
                </>
              ) : (
                <>
                  {chatUrl && (
                    <Grid item xs={12}>
                      <Zoom in={true} timeout={600}>
                        <Box sx={{ p: 2, textAlign: "center" }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: "50%",
                              bgcolor: "#e6f9f2",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 2,
                            }}
                          >
                            <ChatIcon sx={{ fontSize: 36, color: "#16c08e" }} />
                          </Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            fontWeight="600"
                          >
                            Chat Interview Ready!
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Your chat interview has been created successfully.
                            Click the button below to start.
                          </Typography>
                          <Button
                            variant="contained"
                            size="medium"
                            endIcon={<LaunchIcon />}
                            onClick={() => handleUrlClick(chatUrl)}
                            sx={{
                              px: 4,
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: "none",
                              fontSize: "1.1rem",
                              fontWeight: "600",
                              boxShadow: 3,
                              background: `linear-gradient(135deg, #16c08e 0%, #11998e 100%)`,
                              color: "white",
                              "&:hover": {
                                boxShadow: 4,
                                background: `linear-gradient(135deg, #11998e 0%, #16c08e 100%)`,
                              },
                            }}
                          >
                            Start Chat Interview
                          </Button>
                        </Box>
                      </Zoom>
                    </Grid>
                  )}

                  {zoomUrl && (
                    <Grid item xs={12}>
                      <Zoom in={true} timeout={600}>
                        <Box sx={{ p: 2, textAlign: "center" }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: "50%",
                              bgcolor: "#e6f9f2",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mx: "auto",
                              mb: 2,
                            }}
                          >
                            <VideocamIcon
                              sx={{ fontSize: 36, color: "#16c08e" }}
                            />
                          </Box>
                          <Typography
                            variant="h6"
                            gutterBottom
                            fontWeight="600"
                          >
                            Video Interview Ready!
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            Your video interview has been created successfully.
                            Click the button below to join.
                          </Typography>
                          <Button
                            variant="contained"
                            size="medium"
                            endIcon={<LaunchIcon />}
                            onClick={() => handleUrlClick(zoomUrl)}
                            sx={{
                              px: 4,
                              py: 1.5,
                              borderRadius: 2,
                              textTransform: "none",
                              fontSize: "1.1rem",
                              fontWeight: "600",
                              boxShadow: 3,
                              background: `linear-gradient(135deg, #16c08e 0%, #11998e 100%)`,
                              color: "white",
                              "&:hover": {
                                boxShadow: 4,
                                background: `linear-gradient(135deg, #11998e 0%, #16c08e 100%)`,
                              },
                            }}
                          >
                            Join Video Interview
                          </Button>
                        </Box>
                      </Zoom>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
};

export default InterviewPage;
